import { format } from 'date-fns/format';
import { isSameDay } from 'date-fns/isSameDay';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/calendar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { FindOverlay } from '@/components/ui/FindOverlay';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import { Markdown } from '@/components/ui/smoothdown';
import { cn } from '@/lib/utils';
import { formatTime, formatTimeRange } from '@/utils/date';

import { entryElementId, useSummaryFind } from './summary-timeline.find';
import { useSummaryDay } from './summary-timeline.hooks';
import {
  type SummaryTimelineItem,
  useSummaryAppsQuery,
} from './summary-timeline.query';

const MAX_PREVIEW_SECTIONS = 4;

function sectionHeadings(text: string): string[] {
  const lines = text.split('\n').map((line) => line.trim());
  const headings = lines
    .filter((line) => /^#{1,6}\s/.test(line) || /^\*\*[^*]+\*\*:?$/.test(line))
    .map((line) =>
      line
        .replace(/[*_#`]/g, '')
        .replace(/:$/, '')
        .trim()
    );
  if (headings.length > 0) return headings;
  const first = lines.find(Boolean) ?? '';
  return [first.replace(/[*_#`]/g, '').trim()];
}

function formatChars(chars: number): string {
  return chars >= 1000
    ? `${(chars / 1000).toFixed(1)}k chars`
    : `${chars} chars`;
}

function TimelineEntry({
  item,
  day,
  isLast,
  open,
  onOpenChange,
}: {
  item: SummaryTimelineItem;
  day: Date;
  isLast: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const apps = useSummaryAppsQuery(item);
  const headings = sectionHeadings(item.text);
  const hidden = headings.length - MAX_PREVIEW_SECTIONS;
  const startedAt = new Date(item.start ?? item.created_at);

  return (
    <XStack className="gap-6">
      <YStack className="w-20 shrink-0 items-end whitespace-nowrap pt-1 text-sm text-foreground-muted tabular-nums">
        <span>{formatTime(startedAt)}</span>
        {!isSameDay(startedAt, day) && (
          <span className="text-xs">{format(startedAt, 'MMM d')}</span>
        )}
      </YStack>
      <YStack className="relative items-center shrink-0">
        <div className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-foreground-muted" />
        {!isLast && <div className="w-px flex-1 bg-border" />}
      </YStack>
      <Collapsible
        open={open}
        onOpenChange={onOpenChange}
        className={cn('min-w-0 flex-1', !isLast && 'pb-8')}
      >
        <CollapsibleTrigger className="group -mx-2 flex w-full items-start gap-2 rounded-md px-2 py-1 text-left hover:bg-secondary-hover">
          <Icons.chevronRight className="mt-1 size-4 shrink-0 text-foreground-muted transition-transform group-data-[state=open]:rotate-90" />
          <YStack className="min-w-0 gap-1">
            <span className="font-medium">{headings[0]}</span>
            {headings.length > 1 && (
              <span className="truncate text-sm text-foreground-muted">
                {headings.slice(1, MAX_PREVIEW_SECTIONS).join(' · ')}
                {hidden > 0 && ` · +${hidden} more`}
              </span>
            )}
            <span className="text-xs text-foreground-muted">
              {item.start &&
                item.end &&
                `${formatTimeRange(item.start, item.end)} · `}
              {formatChars(item.chars)}
            </span>
            {apps.data && apps.data.length > 0 && (
              <XStack className="flex-wrap gap-1.5 pt-0.5">
                {apps.data.map(({ app, count }) => (
                  <span
                    key={app}
                    title={`${count} captures from ${app} in this window`}
                    className="rounded-full border border-border px-2 py-0.5 text-xs text-foreground-muted"
                  >
                    {app} <span className="tabular-nums">{count}</span>
                  </span>
                ))}
              </XStack>
            )}
          </YStack>
        </CollapsibleTrigger>
        <CollapsibleContent
          id={entryElementId(item.uuid)}
          className="pl-6 pt-3"
        >
          <Markdown id={entryElementId(item.uuid)} content={item.text} />
        </CollapsibleContent>
      </Collapsible>
    </XStack>
  );
}

function DayPicker({
  day,
  label,
  onPick,
}: {
  day: Date;
  label: string;
  onPick: (next: Date) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="ghost" className="gap-1 px-2 text-xl font-medium">
          {label}
          <Icons.chevronDown className="size-4 text-foreground-muted" />
        </Button>
      </Popover.Trigger>
      <Popover.Content className="mt-1 w-auto p-0" align="start">
        <div className="p-3">
          <Calendar
            mode="single"
            selected={day}
            defaultMonth={day}
            disabled={{ after: new Date() }}
            onSelect={(next?: Date) => {
              if (!next) return;
              onPick(next);
              setOpen(false);
            }}
            autoFocus
          />
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

export const SummaryTimeline = observer(function SummaryTimeline() {
  const day = useSummaryDay();
  const containerRef = useRef<HTMLDivElement>(null);
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const setEntryOpen = useCallback((uuid: string, open: boolean) => {
    setOpenIds((prev) => {
      if (prev.has(uuid) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(uuid);
      else next.delete(uuid);
      return next;
    });
  }, []);
  const expand = useCallback(
    (uuid: string) => setEntryOpen(uuid, true),
    [setEntryOpen]
  );
  const find = useSummaryFind(day.items, containerRef, expand);

  return (
    <div className="relative h-full">
      <FindOverlay
        isOpen={find.isOpen}
        query={find.query}
        onQueryChange={find.setQuery}
        caseSensitive={find.caseSensitive}
        onCaseSensitiveChange={find.setCaseSensitive}
        currentMatch={find.currentMatch}
        totalMatches={find.totalMatches}
        onNext={find.goToNext}
        onPrevious={find.goToPrevious}
        onClose={find.close}
      />
      <div ref={containerRef} className="h-full overflow-y-auto">
        <YStack className="mx-auto w-full max-w-4xl px-8 py-8">
          <XStack className="items-center gap-1 pb-8">
            <Button
              variant="ghost"
              size="icon-sm"
              title="Previous day"
              onClick={day.goPrev}
            >
              <Icons.chevronLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              title="Next day"
              disabled={day.isToday}
              onClick={day.goNext}
            >
              <Icons.chevronRight />
            </Button>
            <DayPicker day={day.day} label={day.label} onPick={day.pick} />
            <Button
              variant="ghost"
              size="xs"
              className={cn(day.isToday && 'invisible')}
              onClick={day.goToday}
            >
              Today
            </Button>
            {!day.isLoading && !day.error && (
              <span className="ml-auto text-sm text-foreground-muted">
                {day.items.length}{' '}
                {day.items.length === 1 ? 'summary' : 'summaries'}
              </span>
            )}
          </XStack>

          {day.isLoading && (
            <span className="text-sm text-foreground-muted">Loading...</span>
          )}
          {day.error && (
            <span className="text-sm text-red-500">
              Error: {day.error.message}
            </span>
          )}
          {!day.isLoading && !day.error && day.items.length === 0 && (
            <span className="text-sm text-foreground-muted">
              No summaries yet for {day.label.toLowerCase()}.
            </span>
          )}
          {day.items.map((item, i) => (
            <TimelineEntry
              key={item.uuid}
              item={item}
              day={day.day}
              isLast={i === day.items.length - 1}
              open={openIds.has(item.uuid)}
              onOpenChange={(open) => setEntryOpen(item.uuid, open)}
            />
          ))}
        </YStack>
      </div>
    </div>
  );
});
